// --- START OF FILE App.js ---
import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth, AuthRedirector } from './context/AuthContext.js'; // eslint-disable-line
import AdminApp from './views/AdminApp.js';
import { AdminProvider, AdminContext } from './context/AdminContext.js';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, LineController, BarElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import './styles/Chatbot.css'; 
import { onSnapshot, doc, collection, query, where, orderBy } from 'firebase/firestore';
import './styles/global.css'; 
import { Toaster } from 'react-hot-toast';

// Vistas principales
import Dashboard from './views/Dashboard.js';
import PendingApproval from './views/PendingApproval.js';
import AuthFlow from './views/AuthFlow.js';

// Componentes globales
import LoadingScreen from './components/LoadingScreen.js';
import Footer from './components/Footer.js';
import Chatbot from 'components/Chatbot.js';    
import { db } from './firebase.js';
import NewsPopup from './components/NewsPopup';
import { hasApprovedAgreement } from './utils/agreementAccess.js';
// NewsPopup: only mounted when there are real news items (handled later)

// Firestore connection manager
const FirestoreConnectionManager = () => {
    const [isOffline, setIsOffline] = useState(false);

    useEffect(() => {
        const unsubscribe = onSnapshot(doc(db, 'platform_config/main'), 
            () => setIsOffline(false),
            (error) => {
                if (error && error.code === 'unavailable') {
                    console.warn('Firestore connection lost.');
                    setIsOffline(true);
                }
            }
        );
        return () => unsubscribe();
    }, []);

    if (!isOffline) return null;
    return <div className="offline-banner">Conexión perdida. Intentando reconectar...</div>;
};

// Register ChartJS
ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, LineController, BarElement, Title, Tooltip, Legend, Filler);

const AppContent = () => {
    const { currentUser, isAdmin, isSupport, userRole, loading, userProfile } = useAuth();
        const adminContext = useContext(AdminContext);
        const isAdminLoading = isAdmin && (!adminContext || adminContext.loading);

        const [userNews, setUserNews] = useState([]);
        const [userHasAgreement, setUserHasAgreement] = useState(false);

        const showConnectionManager = currentUser && isAdmin;
        const hasAdminWorkspaceAccess = isAdmin || isSupport;
        const effectiveRole = userRole || (isAdmin ? 'admin' : (isSupport ? 'support' : 'user'));

        // El popup de anuncios es solo para quien tiene un depósito aprobado (ver utils/agreementAccess.js).
        useEffect(() => {
            if (loading || !currentUser || hasAdminWorkspaceAccess) {
                setUserHasAgreement(false);
                return;
            }
            const unsub = onSnapshot(collection(db, `users/${currentUser.uid}/agreements`), (snap) => {
                setUserHasAgreement(hasApprovedAgreement(snap.docs.map(d => d.data())));
            }, (err) => console.error('Error al leer acuerdos para anuncios:', err));
            return () => unsub();
        }, [currentUser, hasAdminWorkspaceAccess, loading]);

        useEffect(() => {
            let unsub;
            if (!loading && !isAdminLoading) {
                if (currentUser && !hasAdminWorkspaceAccess && userHasAgreement) {
                    const q = query(collection(db, 'news'), where('published', '==', true), orderBy('createdAt', 'desc'));
                    unsub = onSnapshot(q, (snap) => {
                        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                        setUserNews(items);
                    }, (err) => console.error('Error al leer noticias para usuario:', err));
                } else {
                    setUserNews([]);
                }
            }
            return () => { if (unsub) unsub(); };
        }, [currentUser, hasAdminWorkspaceAccess, loading, isAdminLoading, userHasAgreement]);

        if (loading || isAdminLoading) return <LoadingScreen />;

        // No news configured by default — do not show popup until we fetch real data
        // Filter out news the user chose to hide (stored in userProfile.hiddenNews)
        const hidden = userProfile?.hiddenNews || [];
        const visibleNews = (userNews || []).filter(n => !hidden.includes(n.id));

        // Aprobación de Usuarios: las cuentas pendientes o rechazadas no entran al panel.
        const approvalStatus = userProfile?.approvalStatus;
        const isBlockedByApproval = !hasAdminWorkspaceAccess && (approvalStatus === 'pending' || approvalStatus === 'rejected');

        return (
            <>
                {currentUser && !hasAdminWorkspaceAccess && !isBlockedByApproval && visibleNews && visibleNews.length > 0 && <NewsPopup news={visibleNews} />}
                        <Routes>
                {currentUser ? (
                    hasAdminWorkspaceAccess ? (
                        <Route path="/*" element={<>
                            {showConnectionManager && <FirestoreConnectionManager />}
                            <AdminApp user={currentUser} userProfile={userProfile} role={effectiveRole} />
                        </>} />
                    ) : isBlockedByApproval ? (
                        <Route path="/*" element={<PendingApproval status={approvalStatus} />} />
                    ) : (
                        <Route path="/*" element={<Dashboard />} />
                    )
                ) : (
                    <>
                       {/* <Route path="/action" element={<ActionHandler />} /> */}
                        <Route path="/*" element={<AuthFlow />} />
                    </>
                )}
            </Routes>
            {currentUser && !hasAdminWorkspaceAccess && <Chatbot />}
        </>
    );
};

// Separado de App() porque necesita useAuth() (saber si hay sesión iniciada, y por lo tanto
// si hay sidebar en pantalla) para decidir si el pie de página debe dejarle lugar al sidebar
// o no — App() está por FUERA de <AuthProvider> (es quien lo instancia), así que no puede
// consumir ese contexto directamente.
// Términos y Condiciones desactivados (2026-10-21): sin link en el pie, sin modal y sin banner de
// aceptación. Se van a reimplementar de otra forma; ver BITACORA.md. Los componentes
// (TermsBanner, TermsContent, AcceptTerms) siguen en el repo sin usarse.
const AppShell = () => {
    const { currentUser } = useAuth();
    // Dashboard y AdminApp (usuario logueado) tienen sidebar propio — el login/registro
    // (AuthFlow, sin sesión) no. El pie de página solo debe correrse a la derecha del sidebar
    // cuando ese sidebar existe de verdad en pantalla.
    const hasSidebar = !!currentUser;

    return (
        <div className={`app-container${hasSidebar ? '' : ' app-container--no-sidebar'}`}>
            <main className="app-main-content">
                <AppContent />
            </main>

            <Footer />
        </div>
    );
};

const App = () => {
    return (
        <Router>
            <AuthProvider>
                <AdminProvider>
                    <Toaster position="top-right" />
                    <AuthRedirector />

                    <AppShell />
                </AdminProvider>
            </AuthProvider>
        </Router>
    );
};

export default App;
